// The "in-edit" body of an EditablePromptCard — the textarea, the byte +
// missing-token validation feedback, the optional reviewer-note input, and
// the Cancel / Publish-or-Send buttons. Extracted out of EditablePromptCard
// to keep the parent under the file-size soft threshold after slice C
// (version history) landed. Pure presentation: all state lives in the
// parent so closing the editor preserves draft text the way it used to.

import { useMemo } from "react";
import { MAX_PROMPT_BYTES, promptByteLength } from "@/lib/aiPrompting/client";
import { EditablePromptCardDryRun } from "./EditablePromptCardDryRun";

export type EditablePromptCardEditorProps = {
  mode: "suggest" | "edit";
  activeText: string;
  placeholders: readonly string[];
  draft: string;
  setDraft: (s: string) => void;
  note: string;
  setNote: (s: string) => void;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  /** Slice D: prompt-key passed to the dry-run endpoint. */
  promptKey: string;
  /** Slice D: only render the Test-draft button when the server says a
   * canonical fixture exists for this prompt_key. */
  dryRunEnabled: boolean;
};

export function EditablePromptCardEditor({
  mode,
  activeText,
  placeholders,
  draft,
  setDraft,
  note,
  setNote,
  busy,
  onCancel,
  onSubmit,
  promptKey,
  dryRunEnabled,
}: EditablePromptCardEditorProps) {
  const missingTokens = useMemo(
    () => placeholders.filter((t) => !draft.includes(t)),
    [draft, placeholders],
  );
  const draftBytes = useMemo(() => promptByteLength(draft), [draft]);
  const overLimit = draftBytes > MAX_PROMPT_BYTES;
  const blockSave = missingTokens.length > 0 || overLimit || !draft.trim();
  const changed = draft !== activeText;

  return (
    <div className="mt-3 space-y-2">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        rows={Math.min(20, Math.max(6, draft.split("\n").length + 1))}
        className="w-full rounded-lg border border-violet-200 bg-white p-3 font-mono text-xs leading-relaxed text-gray-800 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-200 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-100"
      />
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px]">
        {missingTokens.length > 0 ? (
          <p className="font-medium text-rose-700 dark:text-rose-300">
            Required token{missingTokens.length === 1 ? "" : "s"} missing:{" "}
            {missingTokens.map((t, i) => (
              <span key={t}>
                <code className="rounded bg-rose-50 px-1 py-0.5 dark:bg-rose-950/40">{t}</code>
                {i < missingTokens.length - 1 ? ", " : ""}
              </span>
            ))}
            . Save is blocked until restored.
          </p>
        ) : (
          <span aria-hidden />
        )}
        <p
          className={
            overLimit
              ? "shrink-0 font-medium text-rose-700 dark:text-rose-300"
              : "shrink-0 text-gray-500 dark:text-zinc-400"
          }
          aria-live="polite"
        >
          {draftBytes.toLocaleString()} / {MAX_PROMPT_BYTES.toLocaleString()} bytes
          {overLimit ? " — over limit" : ""}
        </p>
      </div>
      {mode === "suggest" ? (
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note for the reviewer"
          className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm text-gray-800 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-200 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-100"
        />
      ) : null}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {dryRunEnabled ? (
          <EditablePromptCardDryRun
            promptKey={promptKey}
            draft={draft}
            disabled={busy || blockSave}
          />
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          className="text-xs rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || !changed || blockSave}
          title={
            blockSave && (missingTokens.length > 0 || overLimit)
              ? missingTokens.length > 0
                ? "Restore the missing token(s) before saving."
                : "Trim the prompt under the byte limit before saving."
              : undefined
          }
          className={
            "text-xs rounded-md px-3 py-1.5 font-semibold disabled:cursor-not-allowed disabled:opacity-50 " +
            (mode === "edit"
              ? "bg-emerald-700 text-white hover:bg-emerald-800 dark:bg-emerald-600 dark:hover:bg-emerald-500"
              : "bg-violet-700 text-white hover:bg-violet-800 dark:bg-violet-600 dark:hover:bg-violet-500")
          }
        >
          {busy ? "Saving…" : mode === "edit" ? "Publish override" : "Send suggestion"}
        </button>
      </div>
    </div>
  );
}
